import { Box, Divider, Grid, Link, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { MapPin, Phone, Mail, Clock, Linkedin, Instagram, Facebook, type LucideIcon } from 'lucide-react'
import { colors, fonts } from '@/theme'
import { brand, categories, contact, industries, legalLinks, resourceLinks, social } from '@/content/site'
import { Container } from '@/components/ui/Container'

const year = new Date().getFullYear()

function ColHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 2.5 }}>
      {children}
    </Typography>
  )
}

const footerLinkSx = { color: 'text.secondary', '&:hover': { color: 'primary.main' } } as const

const socialIcons: Record<string, LucideIcon> = { LinkedIn: Linkedin, Instagram, Facebook }

export function Footer() {
  return (
    <Box component="footer" sx={{ position: 'relative', overflow: 'hidden', bgcolor: colors.surfaceAlt, borderTop: `1px solid ${colors.border}`, mt: 'auto' }}>
      {/* Oversized brand watermark, cropped by the bottom edge */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: { xs: -12, md: -28 },
          textAlign: 'center',
          fontFamily: fonts.display, fontWeight: 600,
          fontSize: { xs: 78, sm: 130, md: 210 },
          lineHeight: 0.8,
          whiteSpace: 'nowrap',
          color: 'rgba(245,245,245,0.035)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        TRUE FORGE
      </Box>
      <Container sx={{ position: 'relative', zIndex: 1, py: { xs: 7, md: 10 } }}>
        <Grid container rowSpacing={{ xs: 5, md: 4 }} columnSpacing={{ xs: 3, md: 4 }}>
          {/* Brand */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box component="img" src={brand.logoMark} alt={`${brand.name} emblem`} width={180} height={180} loading="lazy" decoding="async" sx={{ height: { xs: 128, md: 180 }, width: 'auto', mb: 2.5 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 320 }}>
              {brand.description}
            </Typography>
          </Grid>

          {/* Products */}
          <Grid size={{ xs: 6, md: 2.5 }}>
            <ColHeading>Products</ColHeading>
            <Stack spacing={1.25}>
              {categories.map((cat) => (
                <Link key={cat.slug} component={RouterLink} to={`/products/${cat.slug}`} variant="body2" sx={footerLinkSx}>
                  {cat.name}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Industries */}
          <Grid size={{ xs: 6, md: 2.5 }}>
            <ColHeading>Industries</ColHeading>
            <Stack spacing={1.25}>
              {industries.map((ind) => (
                <Link key={ind.slug} component={RouterLink} to={`/industries/${ind.slug}`} variant="body2" sx={footerLinkSx}>
                  {ind.name}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact */}
          <Grid size={{ xs: 12, md: 3 }}>
            <ColHeading>Contact</ColHeading>
            <Stack spacing={1.75} sx={{ color: 'text.secondary' }}>
              <Stack direction="row" spacing={1.5}><MapPin size={18} /><Typography variant="body2">{contact.address}</Typography></Stack>
              <Stack direction="row" spacing={1.5}><Phone size={18} /><Typography variant="body2">{contact.phone}</Typography></Stack>
              <Stack direction="row" spacing={1.5}><Mail size={18} /><Typography variant="body2">{contact.email}</Typography></Stack>
              <Stack direction="row" spacing={1.5}><Clock size={18} /><Typography variant="body2">{contact.hours}</Typography></Stack>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 4, md: 5 } }} />

        <Stack
          useFlexGap
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', textAlign: 'center', order: { xs: 3, sm: 1 } }}
          >
            © {year} {brand.legalName}. All rights reserved.
          </Typography>

          <Stack
            useFlexGap
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2.5, sm: 4 }}
            alignItems="center"
            sx={{ order: { xs: 1, sm: 2 } }}
          >
            <Stack direction="row" spacing={2.5} alignItems="center" justifyContent="center">
              {social.map((s) => {
                const Icon = socialIcons[s.label]
                return (
                  <Link
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    sx={{ ...footerLinkSx, display: 'inline-flex', p: 0.5, m: -0.5 }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {Icon ? <Icon size={20} /> : s.label}
                  </Link>
                )
              })}
            </Stack>
            <Stack direction="row" spacing={{ xs: 2.5, sm: 3 }} alignItems="center" justifyContent="center" flexWrap="wrap">
              {[...resourceLinks, ...legalLinks].map((l) => (
                <Link key={l.to} component={RouterLink} to={l.to} variant="caption" sx={footerLinkSx}>
                  {l.label}
                </Link>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}
